import type { AriaAttributes } from 'react';
import { Box, IconButton, Stack } from '@mui/material';
import Close from '@mui/icons-material/Close';
import { Badge } from 'framework/ui/mui-builder';
import { findField } from '../../format/elements';
import type { Reference } from '../../format/pieces';
import { useBuilder } from '../context';
import { FieldIcon } from './FieldIcon';
import { fieldName } from './shared';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';

interface ReferenceChipProps extends Pick<AriaAttributes, 'aria-labelledby'> {
  reference: Reference;
  onOpen: (anchor: HTMLElement) => void;
  onClear: () => void;
}

export const ReferenceChip = ({ reference, onOpen, onClear, ...props }: ReferenceChipProps) => {
  const { form } = useBuilder();
  const other = reference.kind === 'fieldValue' ? findField(form, reference.fieldId) : undefined;
  const icon =
    reference.kind === 'today' ? (
      <CalendarMonthOutlinedIcon sx={{ fontSize: 20 }} />
    ) : (
      other && <FieldIcon sx={{ fontSize: 20 }} field={other} />
    );
  return (
    <Box {...props} role="group" sx={{ position: 'relative', width: '100%' }}>
      <Badge
        variant="outlined"
        color="primary"
        onClick={event => onOpen(event.currentTarget)}
        sx={{
          width: '100%',
          minHeight: 38,
          ml: 0,
          fontSize: 'inherit',
          justifyContent: 'flex-start',
        }}
      >
        <Stack component="span" direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {icon}
          <Box>{reference.kind === 'today' ? 'Today' : other ? fieldName(other) : 'A deleted field'}</Box>
        </Stack>
      </Badge>
      <IconButton
        aria-label="Clear reference"
        size="small"
        onClick={onClear}
        sx={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)' }}
      >
        <Close fontSize="small" />
      </IconButton>
    </Box>
  );
};
